/**
 * OAuthTokenStore — in-memory token store for OAuth-authenticated
 * User Management sessions.
 *
 * Separate from the platform's SSO TokenManager so the two auth flows
 * never interfere with each other.
 */

import { CountMetric } from '@amzn/ihm-associate-tools-js-lib'
import { getStoreId } from '@utils/EnvironmentUtil'
import { updateSessionTokens } from './OAuthPopupService'
import type { TokenRefreshResult } from '../TokenProvider'
import { SessionRefreshRejectedError } from './SessionRefreshRejectedError'

const REFRESH_THRESHOLD = 0.75
// Startup waits on a refresh of an expired session, so a stalled token endpoint must not hold the app on its
// loading state indefinitely. A timed-out refresh is a temporary failure: the session is kept and a later refresh
// can still succeed.
export const REFRESH_TIMEOUT_MS = 10000
// Sign-out waits on the revoke with no progress state, so keep a stalled endpoint from holding it for long.
export const REVOKE_TIMEOUT_MS = 3000

export interface IOAuthTokens {
    accessToken: string
    refreshToken?: string
    idToken?: string
    expiresIn: number
    expiresAt?: number
}

export interface IOAuthProviderConfig {
    tokenEndpoint: string
    clientId: string
}

class OAuthTokenStore {
    private static instance: OAuthTokenStore
    private tokens: IOAuthTokens | null = null
    private providerConfig: IOAuthProviderConfig | null = null
    private refreshPromise: Promise<void> | null = null

    public static getInstance (): OAuthTokenStore {
        if (!OAuthTokenStore.instance) {
            OAuthTokenStore.instance = new OAuthTokenStore()
        }
        return OAuthTokenStore.instance
    }

    public setProviderConfig (config: IOAuthProviderConfig): void {
        this.providerConfig = config
    }

    public getProviderConfig (): IOAuthProviderConfig | null {
        return this.providerConfig
    }

    /**
     * Keeps a caller-supplied `expiresAt` — the absolute expiry stamped when the token was issued.
     * Recomputing it from `expiresIn` restarts the clock: a session restored on page reload 50 minutes
     * into a 60-minute token would be treated as valid for another full hour, and the expired token
     * sent until the shifted refresh threshold — the gateway's "Token has expired" bursts.
     * `expiresIn` is only the fallback for callers that have no absolute expiry.
     */
    public setTokens (tokens: IOAuthTokens): void {
        const expiresAt = tokens.expiresAt ?? Date.now() + tokens.expiresIn * 1000
        this.tokens = { ...tokens, expiresAt }
    }

    public async getAccessToken (): Promise<string | null> {
        if (!this.tokens) return null

        if (this.shouldRefresh()) {
            const result = await this.runRefresh()
            if (result.kind === 'refreshed') return result.accessToken
            // A temporary failure keeps the session: use the current token while it is still valid, and the next
            // call tries the refresh again.
            if (result.kind === 'transient' && this.isAuthenticated()) return this.tokens.accessToken
            // The session just ended. Say so, rather than returning null like a caller with no session: this refresh
            // runs before any request is sent, so no 401 would ever reach the sign-out in AuthCoralHandler.
            if (result.kind === 'failed') throw new SessionRefreshRejectedError('[OAuthTokenStore] The session could not be refreshed')
            return null
        }

        return this.tokens?.accessToken || null
    }

    /**
     * Refresh now, regardless of the expiry threshold — for when the gateway has rejected the current
     * access token as unauthorized even though the local expiry says it is still valid. The session is cleared
     * only when the refresh is rejected ('failed'), not on a temporary failure. timeoutMs (default
     * REFRESH_TIMEOUT_MS) aborts the request, so it cannot complete after the caller has given up on it; a
     * refresh already in flight is shared as it is.
     */
    public async forceRefresh (options?: { timeoutMs?: number }): Promise<TokenRefreshResult> {
        if (!this.tokens) return { kind: 'failed' }
        return this.runRefresh(options?.timeoutMs)
    }

    public isAuthenticated (): boolean {
        if (!this.tokens?.expiresAt) {
            return false
        }
        return Date.now() < this.tokens.expiresAt
    }

    /** Returns the raw OIDC ID token (carries profile claims like name/email), or null. */
    public getIdToken (): string | null {
        return this.tokens?.idToken ?? null
    }

    /**
     * Revokes the refresh token at Cognito /oauth2/revoke, so a copy taken before sign-out can no longer get new
     * tokens. Best effort: a failure is counted and sign-out carries on.
     */
    public async revokeRefreshToken (timeoutMs: number = REVOKE_TIMEOUT_MS): Promise<void> {
        const refreshToken = this.tokens?.refreshToken
        if (!refreshToken || !this.providerConfig) return

        const { tokenEndpoint, clientId } = this.providerConfig
        const abortController = new AbortController()
        const timeout = setTimeout(() => abortController.abort(), timeoutMs)
        try {
            // /oauth2/revoke sits next to /oauth2/token on the Cognito domain.
            const response = await fetch(new URL('revoke', tokenEndpoint).toString(), {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({ token: refreshToken, client_id: clientId }).toString(),
                signal: abortController.signal,
            })
            if (!response.ok) {
                throw new Error(`[OAuthTokenStore] Revoke failed (${response.status}): ${await response.text()}`)
            }
        } catch (error) {
            new CountMetric(getStoreId(), 'Fault', 'JWOStoreManagementPlatform', 'TokenRevoke.Failure').count(1)
            console.warn('[OAuthTokenStore] Could not revoke the refresh token', error)
        } finally {
            clearTimeout(timeout)
        }
    }

    public clear (): void {
        this.tokens = null
        this.providerConfig = null
    }

    /**
     * Runs one refresh, shared by concurrent callers. Clears the session only when the refresh is rejected; a
     * temporary failure (network, token endpoint 5xx) keeps it, so a later refresh can still succeed.
     */
    private async runRefresh (timeoutMs: number = REFRESH_TIMEOUT_MS): Promise<TokenRefreshResult> {
        if (!this.refreshPromise) {
            this.refreshPromise = this.refreshAccessToken(timeoutMs).finally(() => {
                this.refreshPromise = null
            })
        }
        try {
            await this.refreshPromise
        } catch (error) {
            if (error instanceof SessionRefreshRejectedError) {
                this.clear()
                return { kind: 'failed' }
            }
            return { kind: 'transient' }
        }
        const accessToken = this.tokens?.accessToken
        return accessToken ? { kind: 'refreshed', accessToken } : { kind: 'failed' }
    }

    private shouldRefresh (): boolean {
        if (!this.tokens?.expiresAt) {
            return false
        }
        const lifetime = (this.tokens.expiresIn || 0) * 1000
        const threshold = lifetime * (1 - REFRESH_THRESHOLD)
        return Date.now() >= this.tokens.expiresAt - threshold
    }

    private async refreshAccessToken (timeoutMs: number): Promise<void> {
        if (!this.tokens?.refreshToken || !this.providerConfig) {
            throw new SessionRefreshRejectedError('[OAuthTokenStore] Cannot refresh — missing refreshToken or providerConfig')
        }

        const { tokenEndpoint, clientId } = this.providerConfig

        const body = new URLSearchParams({
            grant_type: 'refresh_token',
            client_id: clientId,
            refresh_token: this.tokens.refreshToken,
        })

        let data: any
        const abortController = new AbortController()
        const timeout = setTimeout(() => abortController.abort(), timeoutMs)
        try {
            const response = await fetch(tokenEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString(),
                signal: abortController.signal,
            })

            if (!response.ok) {
                const text = await response.text()
                const message = `[OAuthTokenStore] Refresh failed (${response.status}): ${text}`
                // 400/401 is the token endpoint's answer about this session (OAuth 2.0 error responses, e.g.
                // invalid_grant), so retrying cannot help. Anything else — 5xx, 429 — is the endpoint's own trouble.
                throw response.status === 400 || response.status === 401 ? new SessionRefreshRejectedError(message) : new Error(message)
            }

            data = await response.json()
            // A 200 without a usable token is a bad response, not a verdict on the session. Treat it as temporary,
            // before it can overwrite the current tokens and the saved session.
            if (!data?.access_token || !(data.expires_in > 0)) {
                throw new Error('[OAuthTokenStore] Refresh response is missing access_token or expires_in')
            }
        } catch (error) {
            new CountMetric(getStoreId(), 'Fault', 'JWOStoreManagementPlatform', 'TokenRefresh.Failure').count(1)
            throw error
        } finally {
            clearTimeout(timeout)
        }

        const expiresAt = Date.now() + data.expires_in * 1000
        this.tokens = {
            accessToken: data.access_token,
            refreshToken: data.refresh_token || this.tokens.refreshToken,
            // Like refresh_token, id_token is optional in the response; keep the current one if it is omitted, or
            // the saved session would lose the profile claims (name/email) for good.
            idToken: data.id_token || this.tokens.idToken,
            expiresIn: data.expires_in,
            expiresAt,
        }
        // Only sign-in persisted the session, so a reload after a refresh restored the sign-in tokens
        // instead of these. Keep the saved copy current.
        updateSessionTokens({ ...this.tokens, expiresAt })
    }

    private constructor () {}
}

export const oauthTokenStore = OAuthTokenStore.getInstance()
